import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import ModalPix from "./ModalPix";

const isProd = process.env.NODE_ENV === "production";
const API_URL = isProd
  ? "https://api.assaicartola2.shop/api/"
  : "http://localhost:5137/api/";

// -------- Styled Components --------

const Container = styled.div`
  padding: 0 4px;
`;

const SaldoBox = styled.div`
  background: #1a5276;
  color: #fff;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SaldoLabel = styled.span`
  font-size: 0.95rem;
  opacity: 0.85;
`;

const SaldoValor = styled.span`
  font-size: 1.8rem;
  font-weight: bold;
  color: ${(p) => (p.negativo ? "#e74c3c" : "#2ecc71")};
`;

const BtnDepositar = styled.button`
  background: #27ae60;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  &:hover { background: #219a52; }
`;

const BoxSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #dce3e8;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const BoxTitulo = styled.p`
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.9rem;
  &:focus { outline: none; border-color: #2980b9; }
`;

const BtnAzul = styled.button`
  background: #2980b9;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #1a6a9a; }
  &:disabled { background: #aaa; cursor: not-allowed; }
`;

const BtnVermelho = styled.button`
  background: #c0392b;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #a93226; }
  &:disabled { background: #aaa; cursor: not-allowed; }
`;

const MsgSucesso = styled.span`
  font-size: 0.82rem;
  color: #27ae60;
  font-weight: 600;
`;

const MsgAviso = styled.p`
  font-size: 0.78rem;
  color: #e67e22;
  margin: 6px 0 0;
`;

const Secao = styled.h3`
  font-size: 1rem;
  color: #333;
  margin: 16px 0 10px;
  border-bottom: 2px solid #eee;
  padding-bottom: 6px;
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
`;

const Th = styled.th`
  background: #f0f3f4;
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: #444;
  border-bottom: 2px solid #ddd;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  color: #333;
`;

const BadgePix = styled.span`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: bold;
  background: ${(p) =>
    p.status === "APROVADO" ? "#d5f5e3" :
    p.status === "PENDENTE" ? "#fef9e7" :
    p.status === "EXPIRADO" ? "#f9ebea" : "#eee"};
  color: ${(p) =>
    p.status === "APROVADO" ? "#1e8449" :
    p.status === "PENDENTE" ? "#b7950b" :
    p.status === "EXPIRADO" ? "#922b21" : "#777"};
`;

const BadgeSaque = styled.span`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: bold;
  background: ${(p) =>
    p.status === "PAGO"        ? "#d5f5e3" :
    p.status === "PENDENTE"    ? "#fef9e7" :
    p.status === "PROCESSANDO" ? "#d6eaf8" :
    p.status === "REJEITADO"   ? "#f9ebea" :
    p.status === "CANCELADO"   ? "#f2f3f4" : "#eee"};
  color: ${(p) =>
    p.status === "PAGO"        ? "#1e8449" :
    p.status === "PENDENTE"    ? "#b7950b" :
    p.status === "PROCESSANDO" ? "#1a5276" :
    p.status === "REJEITADO"   ? "#922b21" :
    p.status === "CANCELADO"   ? "#777"   : "#555"};
`;

const Vazio = styled.p`
  text-align: center;
  color: #999;
  padding: 20px 0;
  font-size: 0.9rem;
`;

const Erro = styled.p`
  text-align: center;
  color: #c0392b;
  padding: 10px 0;
  font-size: 0.9rem;
`;

// -------- Componente --------

export default function TelaPagamentos({ usuario, onSaldoAtualizado }) {
  const [showPix, setShowPix] = useState(false);

  // Depositos
  const [historico, setHistorico] = useState([]);
  const [carregandoHist, setCarregandoHist] = useState(true);
  const [erroHist, setErroHist] = useState("");

  // Chave PIX
  const [chavePix, setChavePix] = useState(usuario.chavePix || "");
  const [chavePixEditando, setChavePixEditando] = useState(usuario.chavePix || "");
  const [salvandoChave, setSalvandoChave] = useState(false);
  const [chavePixSalva, setChavePixSalva] = useState(false);

  // Saque
  const [valorSaque, setValorSaque] = useState("");
  const [solicitandoSaque, setSolicitandoSaque] = useState(false);
  const [saques, setSaques] = useState([]);
  const [carregandoSaques, setCarregandoSaques] = useState(true);
  const [saqueMsgSucesso, setSaqueMsgSucesso] = useState(false);
  const [saqueMsgErro, setSaqueMsgErro] = useState("");

  const carregarHistorico = useCallback(async () => {
    setCarregandoHist(true);
    setErroHist("");
    try {
      const res = await axios.get(`${API_URL}pix/historico/${usuario.id}`);
      setHistorico(res.data);
    } catch {
      setErroHist("Nao foi possivel carregar o historico.");
    } finally {
      setCarregandoHist(false);
    }
  }, [usuario.id]);

  const carregarSaques = useCallback(async () => {
    setCarregandoSaques(true);
    try {
      const res = await axios.get(`${API_URL}liga/saques/${usuario.id}`);
      setSaques(res.data);
    } catch {
      // silencioso
    } finally {
      setCarregandoSaques(false);
    }
  }, [usuario.id]);

  useEffect(() => {
    carregarHistorico();
    carregarSaques();
  }, [carregarHistorico, carregarSaques]);

  async function salvarChavePix() {
    setSalvandoChave(true);
    try {
      await axios.put(`${API_URL}liga/atualizarChavePix`, {
        idUsuario: usuario.id,
        chavePix: chavePixEditando.trim() || null,
      });
      setChavePix(chavePixEditando.trim());
      setChavePixSalva(true);
      setTimeout(() => setChavePixSalva(false), 3000);
    } catch {
      alert("Erro ao salvar chave PIX. Tente novamente.");
    } finally {
      setSalvandoChave(false);
    }
  }

  async function solicitarSaque() {
    const valor = parseFloat(valorSaque.replace(",", "."));
    if (!valor || valor <= 0) { setSaqueMsgErro("Informe um valor valido."); return; }
    if (valor > saldo) { setSaqueMsgErro("Saldo insuficiente."); return; }
    if (!chavePix) { setSaqueMsgErro("Cadastre sua chave PIX antes de solicitar o saque."); return; }

    setSolicitandoSaque(true);
    setSaqueMsgErro("");
    try {
      await axios.post(`${API_URL}liga/solicitarSaque`, {
        idUsuario: usuario.id,
        valor,
        chavePix,
      });
      setValorSaque("");
      setSaqueMsgSucesso(true);
      setTimeout(() => setSaqueMsgSucesso(false), 4000);
      carregarSaques();
      if (onSaldoAtualizado) onSaldoAtualizado();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao solicitar saque.";
      setSaqueMsgErro(msg);
    } finally {
      setSolicitandoSaque(false);
    }
  }

  function handlePago() {
    setShowPix(false);
    carregarHistorico();
    if (onSaldoAtualizado) onSaldoAtualizado();
  }

  const saldo = usuario.valor ?? 0;
  const chaveAlterada = chavePixEditando.trim() !== chavePix;

  return (
    <Container>
      {/* Saldo */}
      <SaldoBox>
        <div>
          <SaldoLabel>Seu saldo atual</SaldoLabel>
          <br />
          <SaldoValor negativo={saldo < 0}>
            R$ {saldo.toFixed(2).replace(".", ",")}
          </SaldoValor>
        </div>
        <BtnDepositar onClick={() => setShowPix(true)}>
          + Depositar via PIX
        </BtnDepositar>
      </SaldoBox>

      {/* Chave PIX */}
      <BoxSection>
        <BoxTitulo>Chave PIX para recebimento de premios</BoxTitulo>
        <Row>
          <Input
            type="text"
            placeholder="CPF, e-mail, telefone ou chave aleatoria"
            value={chavePixEditando}
            onChange={(e) => setChavePixEditando(e.target.value)}
          />
          <BtnAzul onClick={salvarChavePix} disabled={salvandoChave || !chaveAlterada}>
            {salvandoChave ? "Salvando..." : "Salvar"}
          </BtnAzul>
        </Row>
        {chavePixSalva && (
          <MsgSucesso style={{ marginTop: 6, display: "block" }}>
            Chave PIX salva com sucesso!
          </MsgSucesso>
        )}
        {!chavePix && (
          <MsgAviso>Cadastre sua chave PIX para poder sacar seus premios.</MsgAviso>
        )}
      </BoxSection>

      {/* Solicitar Saque */}
      <BoxSection>
        <BoxTitulo>Solicitar Saque</BoxTitulo>
        {!chavePix ? (
          <MsgAviso>Cadastre sua chave PIX acima para poder sacar.</MsgAviso>
        ) : saldo <= 0 ? (
          <Vazio style={{ padding: "8px 0" }}>Voce nao possui saldo disponivel para saque.</Vazio>
        ) : (
          <>
            <Row>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={saldo}
                placeholder={`Valor (max R$ ${saldo.toFixed(2).replace(".", ",")})`}
                value={valorSaque}
                onChange={(e) => { setValorSaque(e.target.value); setSaqueMsgErro(""); }}
              />
              <BtnVermelho
                onClick={solicitarSaque}
                disabled={solicitandoSaque || !valorSaque}
              >
                {solicitandoSaque ? "Solicitando..." : "Sacar"}
              </BtnVermelho>
            </Row>
            <p style={{ fontSize: "0.78rem", color: "#777", marginTop: 6 }}>
              Chave PIX: <strong>{chavePix}</strong>
            </p>
            {saqueMsgSucesso && (
              <MsgSucesso style={{ display: "block", marginTop: 6 }}>
                Saque solicitado! Sera processado em breve pelo administrador.
              </MsgSucesso>
            )}
            {saqueMsgErro && (
              <p style={{ fontSize: "0.82rem", color: "#c0392b", marginTop: 6 }}>
                {saqueMsgErro}
              </p>
            )}
          </>
        )}
      </BoxSection>

      {/* Historico de saques */}
      {saques.length > 0 && (
        <>
          <Secao>Historico de Saques</Secao>
          <Tabela>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Valor</Th>
                <Th>Chave PIX</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {saques.map((s) => (
                <tr key={s.id}>
                  <Td>{new Date(s.dtSolicitacao).toLocaleDateString("pt-BR")}</Td>
                  <Td>R$ {Number(s.valor).toFixed(2).replace(".", ",")}</Td>
                  <Td style={{ fontSize: "0.8rem", color: "#555" }}>{s.chavePix}</Td>
                  <Td><BadgeSaque status={s.status}>{s.status}</BadgeSaque></Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </>
      )}

      {/* Historico de depositos PIX */}
      <Secao>Historico de Depositos PIX</Secao>

      {carregandoHist && <Vazio>Carregando...</Vazio>}
      {erroHist && <Erro>{erroHist}</Erro>}

      {!carregandoHist && !erroHist && historico.length === 0 && (
        <Vazio>Nenhum deposito PIX registrado ainda.</Vazio>
      )}

      {!carregandoHist && !erroHist && historico.length > 0 && (
        <Tabela>
          <thead>
            <tr>
              <Th>Data</Th>
              <Th>Valor</Th>
              <Th>Status</Th>
              <Th>Pagamento</Th>
            </tr>
          </thead>
          <tbody>
            {historico.map((p) => (
              <tr key={p.id}>
                <Td>{new Date(p.dtCriacao).toLocaleDateString("pt-BR")}</Td>
                <Td>R$ {Number(p.valor).toFixed(2).replace(".", ",")}</Td>
                <Td><BadgePix status={p.status}>{p.status}</BadgePix></Td>
                <Td>
                  {p.dtPagamento
                    ? new Date(p.dtPagamento).toLocaleString("pt-BR")
                    : "-"}
                </Td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}

      {showPix && (
        <ModalPix
          idUsuario={usuario.id}
          onClose={() => setShowPix(false)}
          onPago={handlePago}
        />
      )}
    </Container>
  );
}